<template lang="pug">
  v-app
    BasePageLoadingProgress(:value="isAppLoading && _isMounted")
    BaseSpinner(:value="isAppLoading && !isAppLoadedFirst && !__hydration" zIndex="999" full opaque)
    TheAnalytics
    TheToaster(ref="toaster")
    TheModal(v-if="isLoadModal")
    TheHeader
    template(v-if="isLoggedIn && !isSignUpPage")
      BannerInvestmentDocuments
      BannerEmailVerification
      TheMarketMessages
    BannerMaintenance
    v-content
      router-view
      NavigationNotificationMenu
    TheFooter
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { INACTIVE_LOGOUT_MESSAGE } from 'Config/constans';
import toasterMixin from 'Mixins/toasterMixin';
import maintenanceMixin from 'Mixins/maintenanceMixin';
import { authWSClient } from 'WSClient';
import { getSearchParamBySearch } from 'Utils/url';
import BasePageLoadingProgress from 'Components/Base/BasePageLoadingProgress';
import BaseSpinner from 'Components/Base/BaseSpinner';
import BaseButtonAddEvent from 'Components/Base/BaseButtonAddEvent';
import TheToaster from 'Components/TheToaster/TheToaster';
import LazyRender from 'Components/LazyRender/LazyRender';
import TheAnalytics from 'Components/TheAnalytics/TheAnalytics';
import TheHeader from 'Components/TheHeader/TheHeader';
import TheFooter from 'Components/TheFooter/TheFooter';
import NavigationNotificationMenu from 'Components/NavigationNotificationMenu/NavigationNotificationMenu';

const BannerInvestmentDocuments = () => import(/* webpackChunkName: 'TheModal' */ 'Components/BannerInvestmentDocuments/BannerInvestmentDocuments');
const TheModal = () => import(/* webpackChunkName: 'TheModal' */ 'Components/TheModal/TheModal');
const BannerEmailVerification = () => import(/* webpackChunkName: 'BannerEmailVerification' */ 'Components/BannerEmailVerification/BannerEmailVerification');
const BannerMaintenance = () => import(/* webpackChunkName: 'BannerMaintenance' */ 'Components/BannerMaintenance/BannerMaintenance');
const TheMarketMessages = () => import(/* webpackChunkName: 'TheMarketMessages' */ 'Components/TheMarketMessages/TheMarketMessages');

export default {
  name: 'App',
  components: {
    BaseSpinner,
    BasePageLoadingProgress,
    LazyRender,
    TheAnalytics,
    TheToaster,
    TheHeader,
    TheFooter,
    NavigationNotificationMenu,
    BaseButtonAddEvent,
    BannerInvestmentDocuments,
    BannerEmailVerification,
    BannerMaintenance,
    TheModal,
    TheMarketMessages,
  },
  mixins: [toasterMixin, maintenanceMixin],
  data() {
    return {
      isLoadModal: false,
      inactiveLogoutMessageId: null,
      isAppLoadedFirst: false,
    };
  },
  computed: {
    ...mapGetters('login', [
      'isLoggedIn',
      'tokenRefreshRequired',
      'tokenExpired',
      'logoutLoading',
    ]),
    ...mapGetters('router', [
      'routeLoading',
    ]),
    ...mapGetters('modal', [
      'modal',
    ]),
    ...mapGetters('profile', [
      'profileModel',
    ]),
    isAppLoading() {
      return !this.__prerender && (this.routeLoading || this.logoutLoading);
    },
    isSignUpPage() {
      return (this.$route.name || '').startsWith('sign-up');
    },
  },
  watch: {
    isLoggedIn: {
      handler() {
        if (this.isLoggedIn) {
          authWSClient.open();
          setTimeout(this.checkNPSSurvey, 60000);
        } else authWSClient.close();
      },
      immediate: true,
    },
    async tokenExpired(isExpired) {
      if (isExpired) {
        this.inactiveLogoutMessageId = this.$_toaster_info(INACTIVE_LOGOUT_MESSAGE, false);
      } else {
        this.closeInactiveLogoutMessage();
      }
    },
    modal(modal) {
      if (modal) this.loadModalModule();
      else this.closeInactiveLogoutMessage();
    },
    tokenRefreshRequired(tokenRefreshRequired) {
      if (tokenRefreshRequired) this.setUserActivityListeners();
      else this.removeUserActivityListeners();
    },
    '$route.name': {
      handler: 'setBodyPageClass',
      immediate: true,
    },
    isAppLoading(isAppLoading) {
      if (isAppLoading) this.isAppLoadedFirst = true;
    },
  },
  mounted() {
    this.clearPreload();
    this.setRouter(this.$router);
    this.setViewDimensions();
    this.setAffiliateId();
    window.addEventListener('resize', this.setViewDimensions);
    // Load TheModal module timeout
    setTimeout(this.loadModalModule, 15000);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setViewDimensions);
    this.removeUserActivityListeners();
  },
  methods: {
    ...mapActions('router', [
      'setRouter',
      'getSuperUserStatus',
    ]),
    ...mapActions('login', [
      'checkToken',
    ]),
    ...mapActions('view', [
      'setScreenWidth',
      'setScreenHeight',
      'setPortraitOrientation',
    ]),
    ...mapActions('modal', [
      'setModalNetPromoterScore',
    ]),
    ...mapActions('profile', [
      'updateMetaProfileData',
    ]),
    setViewDimensions() {
      this.setScreenWidth(document.documentElement.clientWidth);
      this.setScreenHeight(document.documentElement.clientHeight);
      this.setPortraitOrientation(this.isPortraitOrientation());
    },
    closeInactiveLogoutMessage() {
      this.$_toaster_hide(this.inactiveLogoutMessageId);
    },
    loadModalModule() {
      this.isLoadModal = true;
    },
    clearPreload() {
      const spinner = document.getElementById('preload-spinner');
      if (spinner) spinner.style.display = 'none';
    },
    setUserActivityListeners() {
      document.addEventListener('mousemove', this.checkTokenOnActive);
      window.addEventListener('scroll', this.checkTokenOnActive);
    },
    removeUserActivityListeners() {
      document.removeEventListener('mousemove', this.checkTokenOnActive);
      window.removeEventListener('scroll', this.checkTokenOnActive);
    },
    checkTokenOnActive() {
      this.checkToken();
      // Remove listeners to avoid duplication calls
      this.removeUserActivityListeners();
    },
    isPortraitOrientation() {
      const orientation = window.matchMedia('(orientation: portrait)');
      return orientation.matches;
    },
    /**
     * Set affiliate id to store to do not send affiliate data to service if user is not affiliated
     */
    setAffiliateId() {
      // Define if this function is ready on app
      window.__checkAffiliateIdFromStore = true;
      const affiliateId = getSearchParamBySearch('affiliate_id', window.location.search);
      if (affiliateId) localStorage.setItem('affiliateId', affiliateId);
    },
    setBodyPageClass() {
      document.body.removeAttribute('class');
      document.body.classList.add(`body-page-${this.$route.name}`);
    },
    setNPSDateToCurrent() {
      const date = new Date().getTime();
      this.updateMetaProfileData({
        data: { lastNPSCheckDate: date },
      });
    },
    checkNPSSurvey() {
      // variable to disable modal for autotests
      const isNPSDisabled = localStorage.getItem('isNPSDisabled');
      if (isNPSDisabled) return;
      // if we started investment process -> ignore
      if (this.$route.name.includes('investment-process')) return;
      // if we have opened any modal -> ignore
      if (!(this.modal === 'ModalNetPromoterScore' || this.modal === null)) return;
      // if modal was opened and page refreshed -> open modal again
      const npsModal = localStorage.getItem('open-nps-survey');
      if (npsModal) {
        this.setModalNetPromoterScore();
        return;
      }
      // if last modal date was not set -> write current date
      if (!this.profileModel.isLastNPSData) {
        this.setNPSDateToCurrent();
        return;
      }
      // if last modal date was less than 90 days ago -> ignore
      if (!this.profileModel.isLastNPSMoreThan90Days) return;
      this.setModalNetPromoterScore();
    },
  },
};
</script>

<style lang="sass">
@import 'styles/app.scss'
#chat-widget-container, #hubspot-messages-iframe-container
  z-index: 8 !important
</style>
